import className from "classnames/bind";
import { useInView } from "motion/react";
import { AnimVals } from '@/utils/animUtils';
import { FeatureExhibitsInteractivesFragment } from '@graphqlTypes';
import { getReduceMotion } from '@/utils/reduceMotion';
import { useEffect, useRef, useState } from "react";
import { Splide, SplideTrack } from "react-splide-ts";

import { CarouselPagination, Container, RevealWrapper, SectionHeader } from "@components";
import { useViewSize } from '@/providers/ViewSizeContext';
import { getExhibitData } from "../utils";

import CarouselExhibitSlide from "./CarouselExhibitSlide";

import styles from "./CarouselExhibitsInteractives.module.scss";
const cx = className.bind(styles);

export default function CarouselExhibitsInteractives(props: FeatureExhibitsInteractivesFragment) {
  const { exhibits, headline, subhead, cta } = props;
  const showHeader = Boolean(headline || subhead);
  const containerRef = useRef<HTMLDivElement>(null);
  const rootEl = useRef<HTMLDivElement>(null);
  const splideRef = useRef<Splide>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canPlay, setCanPlay] = useState(false);

  const exhibitItems = exhibits.map(exhibit => getExhibitData(exhibit)).filter(Boolean);

  // -------------------- Animate In

  const { isDesktop } = useViewSize();
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });
  const isRevealEnabled = true;

  // -------------------- Other stuff

  const updateActiveIndex = () => {
    if (splideRef.current) {
      const { index } = splideRef.current.splide;
      setActiveIndex(index);
    }
  };

  useEffect(() => {
    if (!getReduceMotion()) {
      setCanPlay(isInView);
    }
  }, [isInView]);

  // -------------------- Splide

  const splideOptions = {
    type: 'slide',
    arrows: true,
    pagination: true,
    perPage: 1,
    perMove: 1,
    speed: 500,

    updateOnMove: true,

    // Desktop first (Splide default)
    fixedWidth: '100%',
    gap: '3rem',
    padding: {
      left: '5rem',
      right: '5rem',
    },

    // Responsive options (smaller than desktop)
    breakpoints: {
      // Below 1921px
      1921: {
        fixedWidth: '91.5%',
        gap: '3rem',
        padding: {
          left: '5rem',
          right: '5rem',
        },
      },
      // Below 1025px
      1025: {
        padding: {
          left: '4rem',
          right: '4rem',
        },
        arrows: false
      },
      // Below 769px
      769: {
        fixedWidth: '100%',
        gap: '2rem',
        padding: {
          left: '2rem',
          right: '2rem',
        },
      },
    }
  };

  // Set specific accessibility attribute overrides dictated by the client
  useEffect(() => {
    if (!rootEl.current) return;
    const lists = rootEl.current.querySelectorAll('.splide__list');
    lists.forEach(list => {
      list.setAttribute('role', 'list');
    });
    const slides = rootEl.current.querySelectorAll('.splide__slide');
    slides.forEach(slide => {
      slide.setAttribute('role', 'listitem');
    });
  }, []);

  // -------------------- Render

  return (
    <section className={cx(["carousel-exhibits-interactives"])} ref={rootEl}>
    <RevealWrapper isInView={isInView}>

      {/* Headline & Subhead */}
      {showHeader && (
        <Container>
          <SectionHeader
            headline={headline}
            subhead={subhead}
            isRevealEnabled={isRevealEnabled}
            isInView={isInView}
          />
        </Container>
      )}

      {/* Carousel */}
      <div>
        <Splide
          className={cx("splide")}
          ref={splideRef}
          options={splideOptions}
          onMoved={updateActiveIndex}
          hasTrack={false}
        >
          <SplideTrack>
            {exhibitItems.map((exhibit, index) => {
              return (
                <CarouselExhibitSlide
                  canPlay={canPlay}
                  active={index === activeIndex}
                  exhibit={exhibit}
                  key={index}
                  index={index}
                  isRevealEnabled={isRevealEnabled}
                  isInView={isInView}
                />
              );
            })}
          </SplideTrack>
          <Container className={cx("bottom")}>
            <CarouselPagination ref={containerRef} cta={cta} />
          </Container>
        </Splide>
      </div>

    </RevealWrapper>
    </section>
  );

}
